import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { NativeStoryMedia, StoryMediaAsset } from 'src/app/plugins/native-runtime.plugins';

type MediaFilter = 'all' | 'videos' | 'photos' | 'live';

@Component({
  selector: 'app-story-media-picker',
  templateUrl: './story-media-picker.component.html',
  styleUrls: ['./story-media-picker.component.scss'],
})
export class StoryMediaPickerComponent implements OnInit, OnDestroy {
  @Input() initialFilter: MediaFilter = 'all';
  @ViewChild('systemMediaInput') systemMediaInput?: ElementRef<HTMLInputElement>;
  readonly filters: Array<{ id: MediaFilter; label: string }> = [
    { id: 'all', label: 'Todos' },
    { id: 'videos', label: 'Videos' },
    { id: 'photos', label: 'Fotos' },
    { id: 'live', label: 'Live Photos' },
  ];
  filter: MediaFilter = 'all';
  albums: Array<{ id: string; name: string }> = [{ id: '', name: 'Recientes' }];
  albumId = '';
  albumName = 'Recientes';
  albumPickerOpen = false;
  assets: StoryMediaAsset[] = [];
  selectedAsset: StoryMediaAsset | null = null;
  loading = false;
  exporting = false;
  hasMore = true;
  error = '';
  private requestId = 0;
  private destroyed = false;

  constructor(private modalController: ModalController, private ref: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filter = this.initialFilter;
    void this.loadAssets(true);
    void this.loadAlbums();
  }

  private async loadAlbums(): Promise<void> {
    try {
      const result = await NativeStoryMedia.listAlbums();
      if (!this.destroyed && result.albums?.length) this.albums = result.albums;
    } catch (error) {
      // Recents can still load when the optional album list is unavailable.
      console.warn('Unable to list story albums', error);
    }
    this.detectChanges();
  }

  async chooseFilter(filter: MediaFilter): Promise<void> {
    if (this.filter === filter) return;
    this.filter = filter;
    this.selectedAsset = null;
    await this.loadAssets(true);
  }

  async chooseAlbum(album: { id: string; name: string }): Promise<void> {
    this.albumPickerOpen = false;
    this.albumId = album.id;
    this.albumName = album.name;
    this.selectedAsset = null;
    await this.loadAssets(true);
  }

  async loadAssets(reset = false): Promise<void> {
    if (reset) {
      this.requestId++;
      this.assets = [];
      this.hasMore = true;
      this.error = '';
      this.loading = false;
    }
    if (this.loading || !this.hasMore) {
      return;
    }
    const requestId = this.requestId;
    this.loading = true;
    try {
      const response = await this.withTimeout(NativeStoryMedia.listAssets({
        filter: this.filter,
        albumId: this.albumId,
        offset: this.assets.length,
        limit: 45,
      }), 12000);
      if (requestId !== this.requestId) return;
      this.assets = [...this.assets, ...response.assets];
      this.hasMore = response.hasMore;
    } catch (error) {
      if (requestId === this.requestId) this.error = this.errorMessage(error);
    } finally {
      if (requestId === this.requestId) this.loading = false;
      this.detectChanges();
    }
  }

  retry(): void {
    void this.loadAssets(true);
  }

  openSystemLibrary(): void {
    this.systemMediaInput?.nativeElement.click();
  }

  async onSystemMediaSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const type = file.type.startsWith('video/') || /\.(mov|mp4|m4v)$/i.test(file.name) ? 'video' : 'image';
    await this.modalController.dismiss({ blob: file, filename: file.name, type }, 'selected');
  }

  private withTimeout<T>(request: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('La fototeca tardó demasiado. Inténtalo de nuevo o abre la fototeca de iOS.')), timeoutMs);
      request.then(
        value => { clearTimeout(timer); resolve(value); },
        error => { clearTimeout(timer); reject(error); }
      );
    });
  }

  onContentScroll(event: Event): void {
    const content = event.target as HTMLElement;
    if (content.scrollHeight - content.scrollTop - content.clientHeight < 450) {
      void this.loadAssets();
    }
  }

  select(asset: StoryMediaAsset): void {
    this.selectedAsset = this.selectedAsset?.id === asset.id ? null : asset;
  }

  async confirm(): Promise<void> {
    if (!this.selectedAsset || this.exporting) return;
    this.exporting = true;
    this.error = '';
    let exportUrl = '';
    let dismissed = false;
    try {
      const exported = await NativeStoryMedia.exportAsset({ id: this.selectedAsset.id });
      exportUrl = exported.url;
      const blob = await this.readExport(exported);
      await this.modalController.dismiss({ blob, filename: exported.name, type: exported.type }, 'selected');
      dismissed = true;
    } catch (error) {
      console.error('Unable to import selected story media', error);
      this.error = this.errorMessage(error);
    } finally {
      if (exportUrl) await NativeStoryMedia.releaseExport({ url: exportUrl }).catch(() => undefined);
      this.exporting = false;
      if (!dismissed) this.detectChanges();
    }
  }

  private async readExport(exported: { url: string; size: number; mimeType: string }): Promise<Blob> {
    if (!Number.isSafeInteger(exported.size) || exported.size <= 0) {
      throw new Error('El archivo seleccionado está vacío.');
    }
    const chunks: Uint8Array[] = [];
    let offset = 0;
    while (offset < exported.size) {
      const chunk = await NativeStoryMedia.readExportChunk({
        url: exported.url,
        offset,
        length: Math.min(512 * 1024, exported.size - offset),
      });
      if (!chunk.bytesRead || chunk.bytesRead > exported.size - offset) {
        throw new Error('No se pudo leer el video completo.');
      }
      const binary = atob(chunk.data);
      if (binary.length !== chunk.bytesRead) {
        throw new Error('El archivo seleccionado está incompleto.');
      }
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
      chunks.push(bytes);
      offset += bytes.length;
    }
    return new Blob(chunks, { type: exported.mimeType });
  }

  close(): Promise<boolean> {
    return this.modalController.dismiss(null, 'cancel');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestId++;
  }

  duration(seconds: number): string {
    const total = Math.round(seconds || 0);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  }

  trackAsset(_index: number, asset: StoryMediaAsset): string {
    return asset.id;
  }

  private errorMessage(error: any): string {
    return String(error?.message || error || 'No se pudo abrir la fototeca.').replace(/^Error:\s*/, '');
  }

  private detectChanges(): void {
    if (!this.destroyed) this.ref.detectChanges();
  }
}
